import { FunctionComponent, memo, useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ChannelContext, OrganisationContext, UserContext } from '../../../context';
import { TCHOP_ROOT_URL } from '../../../env';
import { Story, StoryCardTeaserImageStyle } from '../../../services';
import {
  Button,
  ButtonType,
  Check,
  getCssVar,
  getOrganisationDomain,
  IconLabel,
  InputField,
  isUrl,
  ListOpenDirection,
  Select,
  Switch,
  useSupabase,
  withHttpsProtocol,
} from '../../../shared';
import {
  Integration,
  IntegrationInputType,
  INTEGRATIONS_TABLE,
  IntegrationStatus,
  IntegrationType,
  STEADY_FEEDS_TABLE,
} from '../Integrations';
import { getInvalidExtractLinkIndexes, parseExtractLinks, serializeExtractLinks } from './helpers';

import classes from './IntegrationForm.module.scss';

const DUPLICATE_ERROR_CODE = '23505';

const HTTPS_PROTOCOL = 'https://';

const MAX_EXTRACT_LINKS = 10;

interface ExtractLinkChange {
  index: number;
  value: string;
}

interface IntegrationFormProps {
  type: IntegrationType;
  stories: Story[];
  onClose: () => void;
  onCallback: () => void;
  integration?: Integration | null;
  presetStoryId?: number;
  onBack?: () => void;
}

export const IntegrationForm: FunctionComponent<IntegrationFormProps> = memo(
  ({ type, stories, onClose, onCallback, integration, presetStoryId, onBack }) => {
    const { t } = useTranslation();

    const { t: ctxT } = useTranslation('', { keyPrefix: 'integrations' });

    const { supabase } = useSupabase();

    const author_id = useContext(UserContext).userInfo.userData.id;

    const org_id = useContext(OrganisationContext).organisation.id;

    const channel_id = useContext(UserContext).userInfo.userData.channelId;

    const { id: channelId, name: channelName } = useContext(ChannelContext).channel;

    const {
      id: integrationId,
      name: integrationName,
      original_url: integrationUrl,
      input_type: integrationInputType,
      extract_link: integrationExtractLink,
      rename_source_enabled: integrationRenameSourceEnabled,
      rename_source: integrationRenameSource,
      auto_publish: integrationAutoPublish,
      card_style_type: integrationTeaserImageStyle,
      fetch_last_cards: integrationFetchLastCards,
      mix_id: integrationStoryId,
      include_retweets: integrationIncludeRetweets,
      include_replies: integrationIncludeReplies,
    } = { ...integration };

    const editMode = useMemo(() => Boolean(integrationId), [integrationId]);

    const isX = type === IntegrationType.x;

    const isLinkedinProfile = type === IntegrationType.linkedin_profile;

    const isSteady = type === IntegrationType.steady;

    const isRss = type === IntegrationType.rss;

    const isScraping = type === IntegrationType.scraping;

    const renderFetchLastCards = !editMode && !isLinkedinProfile && !isSteady;

    const renderRenameSource = isRss || isScraping;

    const [storyId, setStoryId] = useState<number | undefined>(integrationStoryId ?? presetStoryId);

    const [name, setName] = useState<string>(integrationName ?? '');

    const [url, setUrl] = useState<string>(integrationUrl ?? HTTPS_PROTOCOL);

    const [urlError, setUrlError] = useState<boolean>(false);

    const [inputType, setInputType] = useState<IntegrationInputType>(
      integrationInputType ?? IntegrationInputType.parsed
    );

    const [extractLinks, setExtractLinks] = useState<string[]>(() => {
      const links = parseExtractLinks(integrationExtractLink);

      return links.length ? links : [HTTPS_PROTOCOL];
    });

    const [extractLinkErrorIndexes, setExtractLinkErrorIndexes] = useState<number[]>([]);

    const [renameSourceEnabled, setRenameSourceEnabled] = useState<boolean>(
      integrationRenameSourceEnabled ?? false
    );

    const [renameSource, setRenameSource] = useState<string>(integrationRenameSource ?? '');

    const [renameSourceError, setRenameSourceError] = useState<boolean>(false);

    const [autoPublish, setAutoPublish] = useState<boolean>(integrationAutoPublish ?? true);

    const [includeRetweets, setIncludeRetweets] = useState<boolean>(
      integrationIncludeRetweets ?? false
    );

    const [includeReplies, setIncludeReplies] = useState<boolean>(
      integrationIncludeReplies ?? false
    );

    const [fetchLastCards, setFetchLastCards] = useState<number>(
      isLinkedinProfile ? 3 : integrationFetchLastCards ?? 2
    );

    const renderTeaserImageStyle = useMemo(
      () =>
        [
          IntegrationType.youtube,
          IntegrationType.google_news,
          IntegrationType.rss,
          IntegrationType.scraping,
        ].includes(type),
      [type]
    );

    const [teaserImageStyle, setTeaserImageStyle] = useState<StoryCardTeaserImageStyle | undefined>(
      renderTeaserImageStyle
        ? integrationTeaserImageStyle ?? StoryCardTeaserImageStyle.STANDARD
        : undefined
    );

    const teaserImageStyleItems = useMemo(
      () => [
        {
          id: StoryCardTeaserImageStyle.STANDARD,
          title: t('teasers.standard'),
        },
        {
          id: StoryCardTeaserImageStyle.SMALL_WITH_TEXT,
          title: t('teasers.small'),
        },
        {
          id: StoryCardTeaserImageStyle.SMALL_WITHOUT_TEXT,
          title: t('teasers.small-without-text'),
        },
        {
          id: StoryCardTeaserImageStyle.BIG_WITHOUT_TEXT,
          title: t('teasers.large'),
        },
      ],
      [t]
    );

    const inputTypeItems = useMemo(
      () => [
        { id: IntegrationInputType.parsed, title: ctxT('parsed') },
        { id: IntegrationInputType.feed, title: ctxT('feed') },
      ],
      [ctxT]
    );

    const youtubeUrlError = useCallback(() => {
      const regex = /^(?:https?:\/\/)?(?:www\.)?youtube\.com\/(?:@[\w.%-]+|channel\/[\w.%-]+)\/?$/i;
      return !regex.test(url);
    }, [url]);

    const facebookUrlError = useCallback(() => {
      const regex =
        /^(?:https?:\/\/)?(?:www\.)?facebook\.com\/(?:(?:pg\/|pages\/)[^/]+\/\d+|groups\/[^/]+|[A-Za-z0-9.%-]+)\/?(?:\?.*)?$/i;
      return !regex.test(url);
    }, [url]);

    const linkedinUrlError = useCallback(() => {
      const regex =
        /^(?:https?:\/\/)?(?:www\.)?linkedin\.com\/company\/[A-Za-z0-9_%-]+\/?(?:\?.*)?$/i;
      return !regex.test(url);
    }, [url]);

    const linkedinProfileUrlError = useCallback(() => {
      const regex = /^(?:https?:\/\/)?(?:www\.)?linkedin\.com\/in\/[\w.%-]+\/?(?:\?.*)?$/i;
      return !regex.test(url);
    }, [url]);

    const tiktokUrlError = useCallback(() => {
      const regex = /^(?:https?:\/\/)?(?:www\.)?tiktok\.com\/@[\w.%-]+\/?(?:\?.*)?$/i;
      return !regex.test(url);
    }, [url]);

    const instagramUrlError = useCallback(() => {
      const regex = /^(?:https?:\/\/)?(?:www\.)?instagram\.com\/[\w.%-]+\/?(?:\?.*)?$/i;
      return !regex.test(url);
    }, [url]);

    const xUrlError = useCallback(() => {
      const regex = /^(?:https?:\/\/)?(?:www\.)?(?:x|twitter)\.com\/[\w.%-]+\/?(?:\?.*)?$/i;
      return !regex.test(url);
    }, [url]);

    const bskyUrlError = useCallback(() => {
      const regex = /^https:\/\/bsky\.app\/profile\/[\w.%-]+\/?$/i;
      return !regex.test(url);
    }, [url]);

    const googleNewsUrlError = useCallback(() => {
      const regex = /^(?:https?:\/\/)?(?:www\.)?news\.google\.com\/.*$/i;
      return !regex.test(url?.trim() ?? '');
    }, [url]);

    const appleUrlError = useCallback(() => {
      const regex = /^(?:https?:\/\/)?(?:www\.)?podcasts\.apple\.com\/.*$/i;
      return !regex.test(url?.trim() ?? '');
    }, [url]);

    const steadyUrlError = useCallback(() => {
      const regex = /^https:\/\/steady\.page\/.+\/rss$/i;
      return !regex.test(url?.trim() ?? '');
    }, [url]);

    const rssUrlError = useCallback(() => {
      const trimmedUrl = url?.trim() ?? '';
      return !trimmedUrl || !isUrl(trimmedUrl);
    }, [url]);

    const scrapingUrlError = useCallback(() => {
      const trimmedUrl = url?.trim() ?? '';
      return !trimmedUrl || !isUrl(trimmedUrl);
    }, [url]);

    const urlValidationError = useCallback(() => {
      switch (type) {
        case IntegrationType.rss:
          return rssUrlError();
        case IntegrationType.scraping:
          return scrapingUrlError();
        case IntegrationType.youtube:
          return youtubeUrlError();
        case IntegrationType.facebook:
          return facebookUrlError();
        case IntegrationType.tiktok:
          return tiktokUrlError();
        case IntegrationType.linkedin:
          return linkedinUrlError();
        case IntegrationType.linkedin_profile:
          return linkedinProfileUrlError();
        case IntegrationType.google_news:
          return googleNewsUrlError();
        case IntegrationType.instagram:
          return instagramUrlError();
        case IntegrationType.x:
          return xUrlError();
        case IntegrationType.apple:
          return appleUrlError();
        case IntegrationType.bsky:
          return bskyUrlError();
        case IntegrationType.steady:
          return steadyUrlError();
        default:
          return true;
      }
    }, [
      appleUrlError,
      bskyUrlError,
      facebookUrlError,
      googleNewsUrlError,
      instagramUrlError,
      linkedinProfileUrlError,
      linkedinUrlError,
      rssUrlError,
      scrapingUrlError,
      steadyUrlError,
      tiktokUrlError,
      type,
      xUrlError,
      youtubeUrlError,
    ]);

    const onUrlChange = useCallback((url: string) => {
      setUrl(url);
      setUrlError(false);
    }, []);

    const onExtractLinkChange = useCallback(({ index, value }: ExtractLinkChange) => {
      setExtractLinks((links) =>
        links.map((link, linkIndex) => (linkIndex === index ? value : link))
      );
      setExtractLinkErrorIndexes((indexes) => indexes.filter((errorIndex) => errorIndex !== index));
    }, []);

    const onExtractLinkBlur = useCallback((index: number) => {
      setExtractLinks((links) =>
        links.map((link, linkIndex) => (linkIndex === index ? withHttpsProtocol(link) : link))
      );
    }, []);

    const addExtractLink = useCallback(() => {
      setExtractLinks((links) => [...links, HTTPS_PROTOCOL]);
    }, []);

    const removeExtractLink = useCallback((index: number) => {
      setExtractLinks((links) => links.filter((_, linkIndex) => linkIndex !== index));
      setExtractLinkErrorIndexes((indexes) =>
        indexes
          .filter((errorIndex) => errorIndex !== index)
          .map((errorIndex) => (errorIndex > index ? errorIndex - 1 : errorIndex))
      );
    }, []);

    const onRenameSourceChange = useCallback((value: string) => {
      setRenameSource(value);
      setRenameSourceError(false);
    }, []);

    const formReset = useCallback(() => {
      setStoryId(undefined);
      setName('');
      setUrl(HTTPS_PROTOCOL);
      setRenameSourceEnabled(false);
      setRenameSource('');
      setTeaserImageStyle(StoryCardTeaserImageStyle.STANDARD);
      setAutoPublish(true);
    }, []);

    const getRootTld = useCallback(() => {
      if (TCHOP_ROOT_URL.endsWith('tchop.it')) {
        return 'it';
      }

      if (TCHOP_ROOT_URL.endsWith('tchop-staging.com')) {
        return 'com';
      }

      return 'io';
    }, []);

    const onSaveHandler = useCallback(async () => {
      if (!supabase) {
        return;
      }

      if (!Boolean(storyId)) {
        toast.error(ctxT('story-error'));
        return;
      }

      if (urlValidationError()) {
        toast.error(ctxT('url-error'));
        setUrlError(true);
        return;
      }

      if (isScraping) {
        const invalidExtractLinkIndexes = getInvalidExtractLinkIndexes(extractLinks);

        if (invalidExtractLinkIndexes.length) {
          toast.error(ctxT('extract-link-error'));
          setExtractLinkErrorIndexes(invalidExtractLinkIndexes);
          return;
        }
      }

      if (renderRenameSource && renameSourceEnabled && !renameSource.trim()) {
        toast.error(ctxT('rename-source-error'));
        setRenameSourceError(true);
        return;
      }

      const runSave = () => {
        if (isSteady) {
          const steadyFeed = { name, steady_rss_url: url, default_mix_id: storyId };

          if (editMode) {
            return supabase.from(STEADY_FEEDS_TABLE).update(steadyFeed).eq('id', integrationId);
          }

          return supabase.from(STEADY_FEEDS_TABLE).insert([
            {
              ...steadyFeed,
              tchop_channel_id: channelId,
              channel_name: channelName,
              status: IntegrationStatus.active,
            },
          ]);
        }

        const integration = {
          name,
          original_url: url,
          auto_publish: autoPublish,
          card_style_type: teaserImageStyle,
          ...(isX && { include_retweets: includeRetweets, include_replies: includeReplies }),
          ...(isLinkedinProfile && { include_retweets: includeRetweets }),
          ...(isRss && { input_type: inputType }),
          ...(isScraping && { extract_link: serializeExtractLinks(extractLinks) }),
          ...(renderRenameSource && {
            rename_source_enabled: renameSourceEnabled,
            rename_source: renameSource.trim() || null,
          }),
        };

        if (editMode) {
          return supabase.from(INTEGRATIONS_TABLE).update(integration).eq('id', integrationId);
        }

        return supabase.from(INTEGRATIONS_TABLE).insert([
          {
            ...integration,
            type,
            author_id,
            org_id,
            org_domain: getOrganisationDomain(),
            channel_id,
            mix_id: storyId,
            status: IntegrationStatus.active,
            fetch_last_cards: fetchLastCards,
            tld: getRootTld(),
          },
        ]);
      };

      const { error } = await runSave();

      if (error) {
        const isDuplicate = !editMode && error.code === DUPLICATE_ERROR_CODE;
        toast.error(isDuplicate ? ctxT('duplicate-error') : t('common.error-message'));
        return;
      }

      toast.success(ctxT('save-success'));

      formReset();

      onClose();

      onCallback();
    }, [
      author_id,
      autoPublish,
      channelId,
      channelName,
      channel_id,
      ctxT,
      editMode,
      extractLinks,
      fetchLastCards,
      formReset,
      getRootTld,
      includeReplies,
      includeRetweets,
      inputType,
      integrationId,
      isLinkedinProfile,
      isRss,
      isScraping,
      isSteady,
      isX,
      name,
      onCallback,
      onClose,
      org_id,
      renameSource,
      renameSourceEnabled,
      renderRenameSource,
      storyId,
      supabase,
      t,
      teaserImageStyle,
      type,
      url,
      urlValidationError,
    ]);

    const renderExtractLinks = useMemo(
      () => (
        <>
          {extractLinks.map((link, index) => (
            <div key={`extract-link-${index}`} className={classes['form__extract-link']}>
              <InputField
                label={`${ctxT('extract-link')} ${index + 1}${index === 0 ? '*' : ''}`}
                placeholder={ctxT('extract-link-placeholder')}
                value={link}
                onChange={({ target }) => onExtractLinkChange({ index, value: target.value })}
                onBlur={() => onExtractLinkBlur(index)}
                error={extractLinkErrorIndexes.includes(index)}
              />

              {index > 0 && (
                <IconLabel
                  className={classes['form__extract-link-remove']}
                  iconId={'delete'}
                  iconSize={20}
                  ariaLabel={t('common.remove')}
                  color={getCssVar('--color-danger')}
                  hoverColor={getCssVar('--color-danger-hover')}
                  onClick={() => removeExtractLink(index)}
                />
              )}
            </div>
          ))}

          {extractLinks.length < MAX_EXTRACT_LINKS && (
            <IconLabel
              iconId={'add-plus'}
              iconSize={18}
              label={ctxT('add-extract-link')}
              color={getCssVar('--base-link-text-color')}
              hoverColor={getCssVar('--base-link-text-hover-color')}
              onClick={addExtractLink}
            />
          )}
        </>
      ),
      [
        addExtractLink,
        ctxT,
        extractLinkErrorIndexes,
        extractLinks,
        onExtractLinkBlur,
        onExtractLinkChange,
        removeExtractLink,
        t,
      ]
    );

    const componentsByType = useMemo(() => {
      switch (type) {
        case IntegrationType.x:
          return (
            <>
              <Check
                label={ctxT('include-retweets')}
                checked={includeRetweets}
                onChange={setIncludeRetweets}
              />
              <Check
                label={ctxT('include-replies')}
                checked={includeReplies}
                onChange={setIncludeReplies}
              />
            </>
          );
        case IntegrationType.linkedin_profile:
          return (
            <Check
              label={ctxT('include-reposts')}
              checked={includeRetweets}
              onChange={setIncludeRetweets}
            />
          );
        default:
          return null;
      }
    }, [ctxT, includeReplies, includeRetweets, type]);

    return (
      <div className={classes['form']}>
        <div className={classes['form__title-row']}>
          {onBack && (
            <IconLabel
              iconId={'arrow-left'}
              iconSize={20}
              ariaLabel={t('common.back')}
              color={'#292e4b'}
              hoverColor={getCssVar('--base-link-text-color')}
              onClick={onBack}
              singleColor
            />
          )}
          <div className={classes['form__title']}>
            {t(`common.${editMode ? 'edit' : 'new'}`)} {ctxT(type)} {t('common.integration')}
          </div>
        </div>

        {(!editMode || isSteady) && (
          <Select
            label={t('common.story')}
            items={stories}
            selectedItemId={storyId}
            onChange={setStoryId}
          />
        )}

        <InputField
          label={ctxT('name')}
          value={name}
          onChange={({ target }) => setName(target.value)}
        />

        <InputField
          label={ctxT(`${type}-url`)}
          placeholder={ctxT(`${type}-url-placeholder`)}
          value={url}
          onChange={({ target }) => onUrlChange(target.value)}
          onBlur={() => setUrl(withHttpsProtocol(url))}
          error={urlError}
        />

        {isRss && (
          <Select
            label={ctxT('input-type')}
            items={inputTypeItems}
            selectedItemId={inputType}
            onChange={(inputType) => setInputType(inputType as IntegrationInputType)}
          />
        )}

        {isScraping && renderExtractLinks}

        {renderRenameSource && (
          <>
            <Switch
              label={ctxT('rename-source')}
              checked={renameSourceEnabled}
              onChange={setRenameSourceEnabled}
            />

            <InputField
              value={renameSource}
              maxLength={160}
              onChange={({ target }) => onRenameSourceChange(target.value)}
              disabled={!renameSourceEnabled}
              error={renameSourceError}
            />
          </>
        )}

        {renderFetchLastCards && (
          <>
            <Select
              label={ctxT('fetch-last-cards')}
              items={[
                { id: 0, title: '0' },
                { id: 2, title: '2' },
                { id: 5, title: '5' },
                { id: 10, title: '10' },
                { id: 15, title: '15' },
                { id: 20, title: '20' },
                { id: 50, title: '50' },
              ]}
              selectedItemId={fetchLastCards}
              onChange={(fetchLastCards) => setFetchLastCards(fetchLastCards as number)}
              listMaxHeightRem={10.5}
              staticListPosition
            />

            {isX && (
              <span className={classes['form__fetch-cards-info']}>
                {ctxT('x-fetch-cards-info')}
              </span>
            )}
          </>
        )}

        {renderTeaserImageStyle && (
          <Select
            label={t('common.teaser-style')}
            items={teaserImageStyleItems}
            selectedItemId={teaserImageStyle}
            onChange={setTeaserImageStyle}
            listOpenDirection={ListOpenDirection.top}
          />
        )}

        {!isSteady && (
          <Check label={ctxT('auto-publish')} checked={autoPublish} onChange={setAutoPublish} />
        )}

        {componentsByType}

        <div className={classes['form__buttons']}>
          <Button type={ButtonType.secondary} label={t('common.cancel')} onClick={onClose} />
          <Button
            type={ButtonType.primary}
            label={t(`common.${editMode ? 'save' : 'create'}`)}
            onClick={onSaveHandler}
          />
        </div>
      </div>
    );
  }
);
